// @ts-nocheck
// Estudo 2.0 — Composer unificado (Escrever/Ensinar + builders quiz/caixa/insight).
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  IcoFlame, IcoTeach, IcoQuote, IcoQuestion, IcoQuiz, IcoInbox, IcoTrophy, IcoPage, IcoChat2,
  IcoBold, IcoItalic, IcoList, IcoHeading, IcoBack, IcoPlus, IcoTrash, IcoClose, IcoCheck, IcoLock, IcoTheme,
} from './icons2';
import { computeHeat, HEAT, PrimaryBtn, LICAO_TAG, ENSINO_SCAFFOLD } from './ui2';
import { MentionInput } from '../../mention-input';

const HEAT_NUDGE = [
  'Uma boa faísca já acende a discussão. Quer desenvolver mais?',
  'Tá pegando. Cite um trecho ou destrinche o porquê pra virar Chama.',
  'Chama! Adicione "como aplico" ou um contraponto pra Forjar.',
  'Forjado. Estudo profundo pronto pra ser discutido. 🔥',
];
const HEAT_NUDGE_ENSINO = [
  'Comece pelo conceito. Explique como se o outro nunca tivesse ouvido falar.',
  'Bom começo. Dê um exemplo concreto pra fixar a ideia.',
  'Tá didático! Aponte o erro comum pra blindar quem aprende.',
  'Aula forjada. Clara o bastante pra ensinar de verdade. 🔥',
];

const bigInput = { width: '100%', background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 12, padding: '12px 14px', color: 'var(--ink)', fontSize: 16, lineHeight: 1.55, resize: 'none', outline: 'none', fontFamily: 'var(--font-sans)' };
const addMini = { display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--lapis)', fontSize: 13, fontWeight: 600, padding: '7px 4px', marginTop: 9 };
const composerLabel = { display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 };
const anchorAdd = { display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 14, padding: '8px 13px', borderRadius: 999, border: '1px dashed var(--line-strong)', color: 'var(--ink-3)', fontSize: 13, fontWeight: 500 };
const chipStyle = (active: boolean, color: string, soft: string) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 13px', borderRadius: 999, flexShrink: 0,
  fontSize: 13, fontWeight: 600, background: active ? soft : 'var(--paper-2)', color: active ? color : 'var(--ink-3)',
  border: `1px solid ${active ? color : 'var(--line)'}`, transition: 'all 0.18s',
});

function Toggle({ on, onClick }: any) {
  return <button onClick={onClick} style={{ width: 42, height: 25, borderRadius: 999, background: on ? 'var(--ember)' : 'var(--paper-3)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
    <span style={{ position: 'absolute', top: 3, left: on ? 20 : 3, width: 19, height: 19, borderRadius: '50%', background: on ? '#1a1208' : 'var(--ink-3)', transition: 'left 0.2s' }} />
  </button>;
}

/* ---------- Medidor de temperatura ---------- */
function HeatMeter({ text, teach }: any) {
  const heat = computeHeat(text);
  const nudges = teach ? HEAT_NUDGE_ENSINO : HEAT_NUDGE;
  return (
    <div style={{ padding: '12px 16px', background: 'var(--paper)', borderRadius: 14, border: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: heat.color, fontWeight: 600, fontSize: 13.5, transition: 'color 0.4s' }}>
          <IcoFlame size={16} fill={heat.level >= 2 ? heat.color : 'none'} style={{ filter: heat.level >= 2 ? `drop-shadow(0 0 6px ${heat.glow})` : 'none' }} />
          {teach ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{heat.label}<span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-4)', letterSpacing: '0.04em' }}>· clareza</span></span> : heat.label}
        </span>
        <div style={{ display: 'flex', gap: 5 }}>
          {HEAT.map((h, i) => (
            <span key={h.key} className="mono" style={{ fontSize: 9.5, color: i === heat.level ? heat.color : 'var(--ink-4)', opacity: i <= heat.level ? 1 : 0.4, textTransform: 'uppercase', letterSpacing: '0.03em', transition: 'color 0.3s' }}>{h.label}</span>
          ))}
        </div>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: 'var(--paper-3)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: Math.max(4, heat.score * 100) + '%', borderRadius: 999, background: 'linear-gradient(90deg, #C2703A, var(--ember) 60%, var(--ember-2))', transition: 'width 0.5s cubic-bezier(0.2,0.8,0.2,1)', boxShadow: heat.level >= 2 ? '0 0 10px ' + heat.glow : 'none' }} />
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 9, lineHeight: 1.4 }}>{nudges[heat.level]}</div>
    </div>
  );
}

/* ---------- Toolbar markdown ---------- */
function MdToolbar({ taRef, value, setValue }: any) {
  const apply = (kind: string) => {
    const ta = taRef.current; if (!ta) return;
    const s = ta.selectionStart, e = ta.selectionEnd;
    const sel = value.slice(s, e);
    let out, caret;
    const lineStart = value.lastIndexOf('\n', s - 1) + 1;
    if (kind === 'bold') { out = value.slice(0, s) + '**' + (sel || 'negrito') + '**' + value.slice(e); caret = s + 2 + (sel || 'negrito').length; }
    else if (kind === 'italic') { out = value.slice(0, s) + '*' + (sel || 'itálico') + '*' + value.slice(e); caret = s + 1 + (sel || 'itálico').length; }
    else if (kind === 'head') { out = value.slice(0, lineStart) + '## ' + value.slice(lineStart); caret = e + 3; }
    else if (kind === 'quote') { out = value.slice(0, lineStart) + '> ' + value.slice(lineStart); caret = e + 2; }
    else if (kind === 'list') { out = value.slice(0, lineStart) + '- ' + value.slice(lineStart); caret = e + 2; }
    setValue(out);
    requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(caret, caret); });
  };
  const Btn = ({ k, Icon, label }: any) => (
    <button onClick={() => apply(k)} style={{ width: 38, height: 34, borderRadius: 9, display: 'grid', placeItems: 'center', color: 'var(--ink-2)', background: 'var(--paper-3)' }} aria-label={label}><Icon size={16} /></button>
  );
  return (
    <div className="scroll-x" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <Btn k="bold" Icon={IcoBold} label="Negrito" />
      <Btn k="italic" Icon={IcoItalic} label="Itálico" />
      <Btn k="head" Icon={IcoHeading} label="Título" />
      <Btn k="quote" Icon={IcoQuote} label="Citação" />
      <Btn k="list" Icon={IcoList} label="Lista" />
    </div>
  );
}

/* ---------- Anchor (página/capítulo) ---------- */
function AnchorInput({ page, setPage, chapter, setChapter }: any) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'var(--paper)', border: '1px solid var(--line-strong)', borderRadius: 10, padding: '8px 12px', flex: '0 0 auto' }}>
        <IcoPage size={15} style={{ color: 'var(--ink-3)' }} />
        <input value={page} onChange={(e) => setPage(e.target.value.replace(/[^0-9]/g, ''))} placeholder="página" inputMode="numeric" style={{ width: 64, background: 'none', border: 'none', outline: 'none', color: 'var(--ink)', fontSize: 14 }} />
      </div>
      <input value={chapter} onChange={(e) => setChapter(e.target.value)} placeholder="capítulo / seção (opcional)" style={{ flex: 1, minWidth: 0, background: 'var(--paper)', border: '1px solid var(--line-strong)', borderRadius: 10, padding: '8px 12px', color: 'var(--ink)', fontSize: 14, outline: 'none' }} />
    </div>
  );
}

/* ---------- Builder de QUIZ ---------- */
function QuizBuilder({ data, setData }: any) {
  const set = (patch: any) => setData({ ...data, ...patch });
  const setQ = (i: number, patch: any) => { const qs = data.questions.map((q: any, k: number) => k === i ? { ...q, ...patch } : q); set({ questions: qs }); };
  const uid = () => 'q' + Math.floor(performance.now() * 1000).toString(36);
  const addQ = () => set({ questions: [...data.questions, { id: uid(), text: '', options: [{ id: 'a', text: '', correct: false }, { id: 'b', text: '', correct: false }] }] });
  const delQ = (i: number) => set({ questions: data.questions.filter((_: any, k: number) => k !== i) });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <input value={data.title} onChange={(e) => set({ title: e.target.value })} placeholder="Título do quiz (opcional)" style={{ ...bigInput, fontFamily: 'var(--font-serif)', fontSize: 22 }} />
      {data.questions.map((q: any, i: number) => (
        <div key={q.id} style={{ background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 14, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
            <span className="mono" style={{ fontSize: 11, color: 'var(--lapis)' }}>Pergunta {i + 1}</span>
            <div style={{ flex: 1 }} />
            {data.questions.length > 1 && <button onClick={() => delQ(i)} style={{ color: 'var(--ink-4)', padding: 3 }}><IcoTrash size={15} /></button>}
          </div>
          <textarea value={q.text} onChange={(e) => setQ(i, { text: e.target.value })} rows={2} placeholder="Enunciado da pergunta" style={{ ...bigInput, fontSize: 15, marginBottom: 10 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {q.options.map((o: any, oi: number) => (
              <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => setQ(i, { options: q.options.map((x: any, xi: number) => ({ ...x, correct: xi === oi })) })} style={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${o.correct ? 'var(--sage)' : 'var(--line-strong)'}`, background: o.correct ? 'var(--sage)' : 'transparent', display: 'grid', placeItems: 'center', flexShrink: 0 }} aria-label="Marcar correta">{o.correct && <IcoCheck size={13} style={{ color: '#1a1208' }} />}</button>
                <input value={o.text} onChange={(e) => setQ(i, { options: q.options.map((x: any, xi: number) => xi === oi ? { ...x, text: e.target.value } : x) })} placeholder={`Opção ${oi + 1}`} style={{ flex: 1, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 9, padding: '8px 11px', color: 'var(--ink)', fontSize: 14, outline: 'none' }} />
                {q.options.length > 2 && <button onClick={() => setQ(i, { options: q.options.filter((_: any, xi: number) => xi !== oi) })} style={{ color: 'var(--ink-4)', padding: 3 }}><IcoClose size={14} /></button>}
              </div>
            ))}
          </div>
          {q.options.length < 6 && <button onClick={() => setQ(i, { options: [...q.options, { id: 'o' + q.options.length + i, text: '', correct: false }] })} style={addMini}><IcoPlus size={13} />Opção</button>}
        </div>
      ))}
      {data.questions.length < 20 && <button onClick={addQ} style={{ ...addMini, justifyContent: 'center', padding: '11px', border: '1px dashed var(--line-strong)', borderRadius: 12 }}><IcoPlus size={15} />Adicionar pergunta</button>}
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--ink-2)' }}>
        <Toggle on={data.hasGabarito} onClick={() => set({ hasGabarito: !data.hasGabarito })} />
        Mostrar gabarito após votar
      </label>
    </div>
  );
}

function ElevatedLock({ label, sub }: any) {
  return (
    <div style={{ display: 'grid', placeItems: 'center', textAlign: 'center', padding: '50px 30px', gap: 12 }}>
      <div style={{ width: 56, height: 56, borderRadius: 18, background: 'var(--paper-2)', display: 'grid', placeItems: 'center' }}><IcoLock size={26} style={{ color: 'var(--ink-4)' }} /></div>
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink-2)' }}>{label}</div>
      {sub && <div style={{ fontSize: 13, color: 'var(--ink-4)', lineHeight: 1.5 }}>{sub}</div>}
    </div>
  );
}

function QboxBuilder({ data, setData }: any) {
  const set = (p: any) => setData({ ...data, ...p });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ width: 44, height: 44, borderRadius: 13, background: 'var(--sage-soft)', display: 'grid', placeItems: 'center', flexShrink: 0 }}><IcoInbox size={22} style={{ color: 'var(--sage)' }} /></div>
        <div><div style={{ fontWeight: 600, fontSize: 15.5, color: 'var(--ink)' }}>Abrir caixa de perguntas</div><div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>Membros perguntam anônimo; você responde.</div></div>
      </div>
      <div>
        <label style={composerLabel}>Enunciado</label>
        <textarea value={data.prompt} onChange={(e) => set({ prompt: e.target.value })} maxLength={200} rows={3} placeholder="Sobre o que você quer ouvir as dúvidas?" style={{ ...bigInput, fontSize: 16 }} />
        <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-4)', textAlign: 'right', marginTop: 4 }}>{data.prompt.length}/200</div>
      </div>
      <div>
        <label style={composerLabel}>Fecha em</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {['3 dias', '7 dias', '14 dias'].map((d) => (
            <button key={d} onClick={() => set({ closesIn: d })} style={{ flex: 1, padding: '10px', borderRadius: 10, fontSize: 13.5, fontWeight: 600, background: data.closesIn === d ? 'var(--ember-soft)' : 'var(--paper)', color: data.closesIn === d ? 'var(--ember-2)' : 'var(--ink-3)', border: `1px solid ${data.closesIn === d ? 'var(--ember)' : 'var(--line)'}` }}>{d}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-3)', padding: '10px 12px', background: 'var(--paper)', borderRadius: 10, border: '1px solid var(--line)' }}>
        <IcoLock size={14} style={{ color: 'var(--sage)', flexShrink: 0 }} />
        As perguntas chegam <strong style={{ color: 'var(--ink-2)', fontWeight: 600 }}>anônimas</strong> — só você responde.
      </div>
    </div>
  );
}

function InsightBuilder({ title, setTitle, text, setText, taRef, milestone }: any) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 10, padding: '13px 14px', background: 'var(--ember-soft)', borderRadius: 12, alignItems: 'flex-start' }}>
        <IcoTrophy size={18} style={{ color: 'var(--ember)', flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>Ao publicar, o marco <strong style={{ color: 'var(--ember-2)' }}>{milestone ? milestone.label : 'atual'}</strong> é <strong style={{ color: 'var(--ink)' }}>encerrado</strong> e este vira o fechamento dele.</div>
      </div>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título do insight" maxLength={120} style={{ ...bigInput, fontFamily: 'var(--font-serif)', fontSize: 28, lineHeight: 1.1, border: 'none', background: 'none', padding: 0 }} />
      <MentionInput plain multiline inputRef={taRef} value={text} onChange={setText} rows={8} dropdownPosition="below" placeholder="O que o clube consolidou neste marco? Costure as melhores ideias…" inputStyle={{ ...bigInput, width: '100%', fontSize: 16.5, lineHeight: 1.62, border: 'none', background: 'none', padding: 0 }} />
    </div>
  );
}

/* ---------- COMPOSE SHEET ---------- */
export function ComposeSheet2({ open, onClose, onCreate, isElevated, activeMilestone, initialKind }: any) {
  const [mode, setMode] = useState('escrever');
  const [text, setText] = useState('');
  const [page, setPage] = useState('');
  const [chapter, setChapter] = useState('');
  const [showAnchor, setShowAnchor] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [quoteText, setQuoteText] = useState('');
  const [isQuestion, setIsQuestion] = useState(false);
  const [plusOpen, setPlusOpen] = useState(false);
  const [ensinoConcept, setEnsinoConcept] = useState('');
  const [busy, setBusy] = useState(false);
  const mkQuiz = () => ({ title: '', hasGabarito: true, questions: [{ id: 'q1', text: '', options: [{ id: 'a', text: '', correct: false }, { id: 'b', text: '', correct: false }] }] });
  const [quiz, setQuiz] = useState(mkQuiz());
  const [qbox, setQbox] = useState({ prompt: '', closesIn: '7 dias', anonymous: true });
  const [insTitle, setInsTitle] = useState('');
  const [tema, setTema] = useState('');
  const taRef = useRef<any>(null);

  const goMode = useCallback((m: string) => {
    setMode(m); setPlusOpen(false);
    if (m === 'ensino') setText((prev) => prev.trim() ? prev : ENSINO_SCAFFOLD);
  }, []);
  useEffect(() => { if (open) { setMode(initialKind || 'escrever'); setBusy(false); } }, [open, initialKind]);

  if (!open) return null;

  const actionMode = mode === 'quiz' || mode === 'question-box' || mode === 'insight-final';

  let canPost = false;
  if (mode === 'escrever') canPost = text.trim().length > 0 || (showQuote && quoteText.trim().length > 0);
  else if (mode === 'ensino') canPost = ensinoConcept.trim().length > 0 && text.replace(/##\s.+/g, '').trim().length > 0;
  else if (mode === 'quiz') canPost = quiz.questions.some((q: any) => q.text.trim()) && quiz.questions.every((q: any) => q.options.filter((o: any) => o.text.trim()).length >= 2);
  else if (mode === 'question-box') canPost = qbox.prompt.trim().length > 0;
  else if (mode === 'insight-final') canPost = text.trim().length > 0 || insTitle.trim().length > 0;

  const resetAll = () => {
    setText(''); setQuoteText(''); setIsQuestion(false); setShowQuote(false); setShowAnchor(false);
    setEnsinoConcept(''); setPage(''); setChapter(''); setQuiz(mkQuiz());
    setQbox({ prompt: '', closesIn: '7 dias', anonymous: true }); setInsTitle(''); setTema('');
  };

  const submit = async () => {
    if (!canPost || busy) return;
    let content: any = {}, outKind, pg = page ? parseInt(page) : null;
    if (mode === 'escrever') {
      const body = text.trim();
      const q = showQuote ? quoteText.trim() : '';
      if (isQuestion) { outKind = 'question'; content = { text: body + (q ? `\n\n> ${q}${page ? `\n— p. ${page}` : ''}` : '') }; }
      else if (q) { outKind = 'citation'; content = { text: q, comment: body || null }; }
      else { outKind = 'reflection'; content = { text: body }; }
    }
    else if (mode === 'ensino') { outKind = 'reflection'; content = { text: `${LICAO_TAG} ${ensinoConcept.trim()}\n${text.trim()}` }; }
    else if (mode === 'quiz') { outKind = 'quiz'; content = { title: quiz.title.trim() || null, hasGabarito: quiz.hasGabarito, questions: quiz.questions.filter((q: any) => q.text.trim()).map((q: any) => ({ ...q, options: q.options.filter((o: any) => o.text.trim()) })) }; }
    else if (mode === 'question-box') { outKind = 'question-box'; content = { prompt: qbox.prompt.trim(), closesIn: qbox.closesIn, anonymous: qbox.anonymous }; }
    else if (mode === 'insight-final') { outKind = 'insight-final'; content = { title: insTitle.trim() || null, text: text.trim(), milestoneLabel: activeMilestone ? activeMilestone.label : null }; }
    if (tema.trim()) content.tags = [tema.trim().slice(0, 32)];
    setBusy(true);
    try {
      await onCreate({ kind: outKind, content, page: pg, chapter: chapter.trim() || null });
      resetAll();
    } finally { setBusy(false); }
  };

  const titleByMode: any = { escrever: 'Escrever', ensino: 'Ensinar', quiz: 'Quiz', 'question-box': 'Caixa de perguntas', 'insight-final': 'Insight final' };
  const submitLabel = busy ? 'Publicando…' : mode === 'insight-final' ? 'Publicar e encerrar' : mode === 'ensino' ? 'Publicar lição' : mode === 'quiz' ? 'Publicar quiz' : mode === 'question-box' ? 'Abrir caixa' : isQuestion ? 'Perguntar' : 'Postar';
  const actions = isElevated ? [['quiz', 'Quiz', IcoQuiz], ['question-box', 'Caixa de perguntas', IcoInbox], ['insight-final', 'Insight final', IcoTrophy]] : [['quiz', 'Quiz', IcoQuiz]];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'var(--paper)', display: 'flex', flexDirection: 'column', maxWidth: 460, margin: '0 auto', animation: 'e2FadeUp 0.25s ease both' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 'calc(10px + var(--sat,0px)) 16px 12px', flexShrink: 0, borderBottom: '1px solid var(--line)' }}>
        <button onClick={onClose} style={{ color: 'var(--ink-3)', fontSize: 15, fontWeight: 500 }}>Cancelar</button>
        <div style={{ flex: 1, textAlign: 'center' }}><span style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink)' }}>{titleByMode[mode]}</span></div>
        <PrimaryBtn disabled={!canPost || busy} onClick={submit} style={{ padding: '8px 18px', fontSize: 14 }}>{submitLabel}</PrimaryBtn>
      </header>

      <div style={{ position: 'relative', padding: '12px 16px', flexShrink: 0, borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 10 }}>
        {actionMode ? (
          <button onClick={() => goMode('escrever')} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 999, background: 'var(--paper-2)', border: '1px solid var(--line)', color: 'var(--ink-2)', fontSize: 13.5, fontWeight: 600 }}>
            <IcoBack size={15} />Voltar a escrever
          </button>
        ) : (
          <>
            <div style={{ display: 'flex', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 999, padding: 3, gap: 2 }}>
              {[['escrever', 'Escrever', null], ['ensino', 'Ensinar', IcoTeach]].map(([m, label, Icon]: any) => {
                const on = mode === m;
                return (
                  <button key={m} onClick={() => goMode(m)} style={{ padding: '7px 16px', borderRadius: 999, fontSize: 13.5, fontWeight: 600, background: on ? 'var(--paper-3)' : 'transparent', color: on ? 'var(--ink)' : 'var(--ink-3)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {Icon && <Icon size={14} style={{ color: on ? 'var(--lapis)' : 'var(--ink-4)' }} />}{label}
                  </button>
                );
              })}
            </div>
            <div style={{ flex: 1 }} />
            <button onClick={() => setPlusOpen((v) => !v)} style={{ width: 38, height: 38, borderRadius: 11, display: 'grid', placeItems: 'center', background: plusOpen ? 'var(--paper-3)' : 'var(--paper-2)', border: '1px solid var(--line)', color: 'var(--ink-2)' }} aria-label="Outros formatos"><IcoPlus size={18} /></button>
          </>
        )}
        {plusOpen && !actionMode && (
          <>
            <div onClick={() => setPlusOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1 }} />
            <div style={{ position: 'absolute', top: 56, right: 16, zIndex: 2, background: 'var(--paper-2)', border: '1px solid var(--line-strong)', borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.4)', padding: 6, minWidth: 210, animation: 'e2FadeUp 0.18s ease both' }}>
              <div className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '6px 10px 4px' }}>Formatos de ação</div>
              {actions.map(([m, label, Icon]: any) => (
                <button key={m} onClick={() => goMode(m)} style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '11px 10px', borderRadius: 10, color: 'var(--ink)', fontSize: 14.5, fontWeight: 500, textAlign: 'left' }}>
                  <span style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--paper-3)', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon size={16} style={{ color: 'var(--ember-2)' }} /></span>{label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px' }}>
        {mode === 'escrever' && (
          <div>
            <MentionInput plain multiline inputRef={taRef} value={text} onChange={setText} dropdownPosition="below" placeholder={isQuestion ? 'Qual pergunta vai abrir a melhor discussão do clube?' : 'Escreva seu estudo, reflexão ou pensamento. O que esse trecho mexeu em você?'} inputStyle={{ ...bigInput, width: '100%', minHeight: showQuote ? 130 : 210, fontSize: isQuestion ? 18.5 : 16.5, lineHeight: 1.6, border: 'none', padding: 0, background: 'none', fontWeight: isQuestion ? 500 : 400 }} />
            <div className="scroll-x" style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={() => { setShowQuote((v) => !v); setShowAnchor(false); }} style={chipStyle(showQuote, 'var(--plum)', 'var(--plum-soft)')}><IcoQuote size={14} />Citar trecho</button>
              <button onClick={() => setIsQuestion((v) => !v)} style={chipStyle(isQuestion, 'var(--sage)', 'var(--sage-soft)')}><IcoQuestion size={14} />Pergunta aberta</button>
              {!showQuote && <button onClick={() => setShowAnchor((v) => !v)} style={chipStyle(showAnchor, 'var(--ink-2)', 'var(--paper-3)')}><IcoPage size={14} />Página</button>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 999, padding: '7px 13px' }}>
              <IcoTheme size={14} style={{ color: 'var(--ink-3)', flexShrink: 0 }} />
              <input value={tema} onChange={(e) => setTema(e.target.value)} maxLength={32} placeholder="tema/assunto (opcional, vira um Fio)" style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none', color: 'var(--ink)', fontSize: 13.5 }} />
            </div>
            {showQuote && (
              <div style={{ marginTop: 14, padding: '13px 14px', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 14 }}>
                <label style={composerLabel}><IcoQuote size={13} style={{ color: 'var(--plum)' }} />Trecho do livro</label>
                <textarea value={quoteText} onChange={(e) => setQuoteText(e.target.value)} rows={3} placeholder="Transcreva o trecho que merece ficar…" style={{ ...bigInput, fontFamily: 'var(--font-serif)', fontSize: 20, lineHeight: 1.32, borderLeft: '2px solid var(--plum)' }} />
                <div style={{ marginTop: 10 }}><AnchorInput page={page} setPage={setPage} chapter={chapter} setChapter={setChapter} /></div>
              </div>
            )}
            {showAnchor && !showQuote && <div style={{ marginTop: 14 }}><AnchorInput page={page} setPage={setPage} chapter={chapter} setChapter={setChapter} /></div>}
            <div style={{ marginTop: 18, fontSize: 12, color: 'var(--ink-4)', lineHeight: 1.5, display: 'flex', gap: 8 }}>
              <IcoFlame size={14} style={{ color: 'var(--ink-4)', flexShrink: 0, marginTop: 1 }} />
              <span>Escreva à vontade — o conteúdo define o tipo. Cite um trecho ou marque como pergunta se quiser. Quanto mais fundo, mais quente fica. 🔥</span>
            </div>
          </div>
        )}

        {mode === 'ensino' && (
          <div>
            <div style={{ display: 'flex', gap: 10, padding: '11px 13px', background: 'var(--lapis-soft)', borderRadius: 12, marginBottom: 16, alignItems: 'flex-start' }}>
              <IcoTeach size={18} style={{ color: 'var(--lapis)', flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>Ensine um conceito com <strong style={{ color: 'var(--ink)' }}>suas próprias palavras</strong>. Quem explica, aprende em dobro — e o grupo aprende com você.</div>
            </div>
            <label style={composerLabel}><IcoTeach size={13} style={{ color: 'var(--lapis)' }} />Conceito que vou ensinar</label>
            <input value={ensinoConcept} onChange={(e) => setEnsinoConcept(e.target.value)} maxLength={80} placeholder="Ex.: Juros compostos, Gatilho da escassez…" style={{ ...bigInput, fontFamily: 'var(--font-serif)', fontSize: 24, lineHeight: 1.2, marginBottom: 16 }} />
            <label style={composerLabel}><IcoChat2 size={13} style={{ color: 'var(--lapis)' }} />Sua aula <span style={{ color: 'var(--ink-4)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>· explique como se o outro nunca tivesse ouvido falar</span></label>
            <MentionInput plain multiline inputRef={taRef} value={text} onChange={setText} dropdownPosition="below" inputStyle={{ ...bigInput, width: '100%', minHeight: 220, fontSize: 16, lineHeight: 1.62 }} />
            {showAnchor
              ? <div style={{ marginTop: 12 }}><AnchorInput page={page} setPage={setPage} chapter={chapter} setChapter={setChapter} /></div>
              : <button onClick={() => setShowAnchor(true)} style={anchorAdd}><IcoPage size={14} />Ancorar numa página</button>}
          </div>
        )}

        {mode === 'quiz' && (isElevated
          ? <QuizBuilder data={quiz} setData={setQuiz} />
          : <ElevatedLock label="Quiz é exclusivo de líderes" sub="Ou o clube precisa permitir quiz de membros." />)}

        {mode === 'question-box' && (isElevated
          ? <QboxBuilder data={qbox} setData={setQbox} />
          : <ElevatedLock label="Caixa de perguntas é exclusiva de líderes" />)}

        {mode === 'insight-final' && (isElevated
          ? <InsightBuilder title={insTitle} setTitle={setInsTitle} text={text} setText={setText} taRef={taRef} milestone={activeMilestone} />
          : <ElevatedLock label="Insight final é exclusivo de líderes" />)}
      </div>

      {(mode === 'escrever' || mode === 'ensino' || mode === 'insight-final') && (
        <div style={{ flexShrink: 0, borderTop: '1px solid var(--line)', background: 'var(--paper-2)', padding: '12px 16px calc(12px + var(--sab,0px))', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <MdToolbar taRef={taRef} value={text} setValue={setText} />
          <HeatMeter text={text} teach={mode === 'ensino'} />
        </div>
      )}
    </div>
  );
}
